import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useState } from 'react';
import { View } from 'react-native';

import { FieldBlock } from '@/components/services/wizard/field';
import { ThemedText } from '@/components/themed-text';
import { Input } from '@/components/ui/input';
import { PressableScale } from '@/components/ui/pressable-scale';
import { cn } from '@/utils/cn';

const PRIMARY = '#FD5001';

interface Props {
  /** Endereço que vai no pedido. */
  value: string;
  onChange: (address: string) => void;
  /** Último endereço que o cliente usou — vira a pergunta "usar o mesmo?". */
  saved?: string | null;
  editable?: boolean;
}

/**
 * Onde o profissional vai atender, em serviço em que ele vai até o cliente.
 * Opcional: sem endereço, os dois combinam direto. Com um endereço salvo, a
 * pergunta é só "usar o mesmo?" — digitar de novo é a exceção.
 */
export function ClientAddressField({ value, onChange, saved, editable = true }: Props) {
  const savedAddress = saved?.trim() || null;
  const [useSaved, setUseSaved] = useState(!!savedAddress);

  // O salvo chega depois (storage/perfil): nasce escolhido se o campo está vazio.
  useEffect(() => {
    if (savedAddress && !value) {
      setUseSaved(true);
      onChange(savedAddress);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedAddress]);

  const input = (
    <Input
      placeholder="Rua, número, bairro e cidade"
      value={useSaved ? '' : value}
      onChangeText={onChange}
      autoComplete="street-address"
      editable={editable}
    />
  );

  if (!savedAddress) {
    return (
      <FieldBlock title="Onde vai ser o atendimento?" note="O profissional vai até você">
        {input}
      </FieldBlock>
    );
  }

  const options = [
    { key: 'saved', label: 'Mesmo endereço', meaning: savedAddress },
    { key: 'other', label: 'Outro endereço', meaning: undefined },
  ] as const;

  return (
    <FieldBlock title="Usar o mesmo endereço?" note="O profissional vai até você">
      <View
        className="overflow-hidden rounded-xl border border-neutral-900/5"
        style={{ borderCurve: 'continuous' }}
      >
        {options.map((option, index) => {
          const selected = (option.key === 'saved') === useSaved;
          return (
            <PressableScale
              key={option.key}
              haptic
              disabled={!editable}
              accessibilityRole="radio"
              accessibilityState={{ selected }}
              onPress={() => {
                const pickSaved = option.key === 'saved';
                setUseSaved(pickSaved);
                onChange(pickSaved ? savedAddress : '');
              }}
              className={cn(
                'flex-row items-center gap-3 px-3.5 py-3',
                index > 0 ? 'border-t border-neutral-100' : '',
                selected ? 'bg-primary/5' : 'bg-card',
              )}
            >
              <View className="flex-1 gap-0.5">
                <ThemedText
                  className={cn(
                    'text-sm leading-5',
                    selected ? 'font-semibold text-neutral-900' : 'text-neutral-700',
                  )}
                >
                  {option.label}
                </ThemedText>
                {option.meaning ? (
                  <ThemedText className="text-xs leading-4 text-neutral-500" numberOfLines={2}>
                    {option.meaning}
                  </ThemedText>
                ) : null}
              </View>
              {selected ? (
                <Ionicons name="checkmark-circle" size={20} color={PRIMARY} />
              ) : (
                <View className="h-5 w-5 rounded-full border-2 border-neutral-200" />
              )}
            </PressableScale>
          );
        })}
      </View>
      {!useSaved && input}
    </FieldBlock>
  );
}
